/**
 * UserMultiSelect — chip-input with server-backed autocomplete.
 *
 * History:
 *   - Phase 2: initial cmdk + Radix Popover combobox.
 *   - Bug D15 (commit 5c7cd949): dropped the `onBlur → commitDraft` race with
 *     row onSelect; added a browse list on empty-query open.
 *   - Bug G1 (this rewrite): the "appears and disappears" flicker and the
 *     "clicking a selected user removes it" bugs both traced to a dual
 *     source of truth for the popover's `open` state. Specifically,
 *     `<PopoverTrigger asChild>` wrapping our interactive chip row layered
 *     Radix's auto-toggle onto every click — clicking a chip, the input,
 *     or anywhere in the row toggled open, and then our wrapper `onClick`
 *     forced `setOpen(true)` back. Rapid clicks (or any click that Radix
 *     considered a close intent) raced the controlled `open` boolean and
 *     produced the flicker. Clicking a row also triggered the trigger's
 *     built-in toggle and Radix' outside-click-sentinel because of how
 *     portal bubbling is re-emitted.
 *
 *     Fix: use `<PopoverAnchor>` (positioning-only, no toggle handlers)
 *     instead of `<PopoverTrigger asChild>`. Open/close is driven solely
 *     by:
 *       - trigger wrapper onClick → setOpen(true)
 *       - PopoverContent onInteractOutside → setOpen(false)
 *       - X-on-chip stopPropagation → doesn't toggle the popover
 *       - row onSelect → keeps popover open (Gmail/Slack pattern)
 *
 *     Additional G1 improvements:
 *       - Each row renders an explicit checkbox that reflects selection
 *         state, so the user can re-click to DEselect without removing
 *         the chip from the trigger bar.
 *       - "Clear all" link shown when value.length > 0.
 *       - Test/service users are filtered server-side in
 *         `/salescoach/api/users/search` — see `users-queries.ts`. Admin
 *         Slack accounts are too, but only while the global Admins filter
 *         says Excluded: the `admins` prop forwards it, so the names on
 *         offer are the population the tables actually show (2026-08-19).
 *
 *   - Batch-6 G (this edit): "keep typing across picks". Previously
 *     `onSelect` called `setDraft('')` to clear the typed text after a
 *     pick. That works for one-at-a-time selection but fights the
 *     common "type a prefix → pick several matches" flow (Gmail/Slack
 *     chips both keep the query). Now the draft survives the pick, the
 *     popover stays open, and the hit list stays narrowed to the same
 *     prefix so the user can keep clicking matches. Placeholder +
 *     internal strings renamed from "slack_username" to "Username" —
 *     slack_username is an internal DB column name, not a UI concept.
 *
 * External contract (UNCHANGED):
 *   value: string[]; onChange(next: string[])
 */

import { Check, Users, X } from 'lucide-react';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { useUserSearch } from '@/hooks/use-user-search';
import { cn } from '@/lib/utils';

export interface UserMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * Stacked mobile variant used inside FilterModal — drops the inline "Users:"
   * label (modal renders its own above), stretches the chip row to full width
   * with a taller 44px min-height, and widens the popover to match. Desktop
   * FilterBar leaves this undefined for the inline row layout.
   */
  stacked?: boolean;
  /**
   * The FilterBar's global Admins control, forwarded to the autocomplete so
   * the names it offers are the same population the tables show. Defaults to
   * the filter's own default ('excluded'), which is the behaviour every
   * caller had before that control existed.
   */
  admins?: 'excluded' | 'included';
}

function initialsOf(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '??';
  const parts = trimmed.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function UserMultiSelect({
  value,
  onChange,
  stacked = false,
  admins = 'excluded',
}: UserMultiSelectProps) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // `browseWhenEmpty: open` — once the popover is open and the input is
  // empty, fetch a 50-row browse list so the user sees SOMETHING without
  // having to guess a prefix. Hook debounces at 200 ms server-side.
  const { data: hits } = useUserSearch(draft, {
    browseWhenEmpty: open,
    admins,
  });

  const selected = useMemo(() => new Set(value), [value]);

  const toggleUsername = useCallback(
    (username: string) => {
      const trimmed = username.trim();
      if (!trimmed) return;
      if (selected.has(trimmed)) {
        onChange(value.filter((u) => u !== trimmed));
      } else {
        onChange([...value, trimmed]);
      }
    },
    [value, onChange, selected],
  );

  const commitDraftLiteral = useCallback(() => {
    // Split on comma so a paste like "alice, bob" yields two chips in one go.
    const pieces = draft
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !value.includes(s));
    if (pieces.length > 0) onChange([...value, ...pieces]);
    setDraft('');
  }, [draft, value, onChange]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Let cmdk handle Enter when the dropdown has highlighted a row —
      // the row's onSelect will toggle the selection. Only fall back to
      // commitDraftLiteral when there are no suggestions AND the user
      // has typed something.
      if (hits.length === 0 && draft.trim()) {
        e.preventDefault();
        commitDraftLiteral();
      }
      // Swallow the form-submit default so an enclosing <form> doesn't submit.
      e.preventDefault();
    } else if (e.key === ',') {
      e.preventDefault();
      commitDraftLiteral();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
    // Escape intentionally handled by Radix Popover (closes on Escape).
  };

  const removeAt = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  // Mobile stacked variant: the modal renders its own "Users" label above,
  // so we skip the inline label; the anchor row stretches full-width with a
  // 44px min height for touch targets, and the popover content widens to
  // match the modal width (320px was fine on desktop but cramped on 375px
  // iPhone SE after a few chips pushed it off-screen).
  const wrapperClass = stacked ? 'flex flex-col gap-2' : 'flex items-center gap-1.5';
  const anchorClass = stacked
    ? 'flex min-h-11 w-full flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-2 cursor-text'
    : 'flex h-8 flex-wrap items-center gap-1 rounded-md border bg-background pl-1.5 pr-2 min-w-[200px] cursor-text';
  const popoverClass = stacked
    ? 'p-0 w-[calc(100vw-2rem)] max-w-sm'
    : 'p-0 w-[320px]';

  return (
    <div className={wrapperClass}>
      {!stacked && (
        <Label htmlFor="filter-users" className="sr-only">
          Users:
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        {/*
          PopoverAnchor (NOT PopoverTrigger) — anchors the popover to this
          element for positioning only, WITHOUT installing Radix's auto-toggle
          click/keyboard handlers. That way a chip's × button, the text
          input's focus, and the wrapper's own onClick each have exactly
          one effect on `open` instead of fighting Radix's built-in toggle.
        */}
        <PopoverAnchor asChild>
          <div
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-owns="user-search-listbox"
            className={cn(anchorClass)}
            onClick={() => {
              inputRef.current?.focus();
              setOpen(true);
            }}
          >
            {!stacked && (
              <Users
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            )}
            {value.map((u, idx) => (
              <Badge key={u} variant="secondary" className="gap-1">
                {u}
                <button
                  type="button"
                  className="ml-0.5 rounded-sm hover:bg-muted"
                  aria-label={`Remove ${u}`}
                  onMouseDown={(e) => {
                    // Stop Radix' pointerdown-based outside-click sentinel
                    // from treating this chip-remove as a close. Without
                    // this, clicking × while the popover is open would
                    // dismiss the popover BEFORE our click handler fires.
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(idx);
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <input
              id="filter-users"
              ref={inputRef}
              type="text"
              aria-label="Search users"
              className="h-7 border-0 shadow-none outline-none bg-transparent p-0 flex-1 min-w-[100px] text-sm"
              placeholder={value.length === 0 ? 'Username,...' : ''}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
            />
            {value.length > 0 && (
              <button
                type="button"
                className="ml-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
                aria-label="Clear all selected users"
              >
                Clear
              </button>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className={popoverClass}
          align="start"
          // Don't steal focus — the chip row's <input> stays the active
          // typing surface.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Let outside clicks close (match shadcn Command pattern) but
          // ignore the synthetic pointerdown that originates from the
          // trigger wrapper itself — otherwise clicking into the input
          // would immediately close the popover we just opened.
          onInteractOutside={(e) => {
            const target = e.target as Node | null;
            const anchor = inputRef.current?.closest('[role="combobox"]');
            if (anchor && target && anchor.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          <Command
            id="user-search-listbox"
            // Tell cmdk we drive the filter ourselves (server does ILIKE).
            shouldFilter={false}
          >
            {/* Hidden CommandInput bound to `draft` so cmdk's highlight/
                keyboard-navigation state tracks the real typing cursor
                even though the visible input is outside the popover. */}
            <CommandInput
              value={draft}
              onValueChange={setDraft}
              className="sr-only"
              aria-hidden
              tabIndex={-1}
              placeholder="Type to search…"
            />
            <CommandList>
              <CommandEmpty>
                {draft.trim() ? 'No matching users' : 'No users available'}
              </CommandEmpty>
              {hits.length > 0 && (
                <CommandGroup>
                  {hits.map((hit) => {
                    const isSelected = selected.has(hit.username);
                    return (
                      <CommandItem
                        key={hit.slackId}
                        // cmdk's default filter matches on `value`; the
                        // visual label is whatever we render inside.
                        value={hit.username}
                        onSelect={() => {
                          toggleUsername(hit.username);
                          // KEEP `draft` as-is. Gmail/Slack pattern: after
                          // picking a row the user usually wants to pick
                          // another match of the same prefix (e.g. type
                          // "ja" → pick "jan" → still want "jared" in the
                          // same list). Clearing would narrow the list to
                          // nothing and force a re-type. Popover also
                          // stays open; cmdk's highlight stays on the
                          // same row (the row is still the best match
                          // for the unchanged query).
                          inputRef.current?.focus();
                          // Ensure the popover stays open even if Radix
                          // saw this click as a "select-and-close" intent.
                          if (!open) setOpen(true);
                        }}
                        className="gap-2"
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded-sm border',
                            isSelected
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'opacity-60',
                          )}
                          aria-hidden
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                        <Avatar>
                          <AvatarFallback seed={hit.username}>
                            {initialsOf(hit.realName || hit.username)}
                          </AvatarFallback>
                        </Avatar>
                        {/* BUG-4: primary line = realName in white (text-foreground),
                            secondary = @username in muted grey, shown ONLY when the
                            handle is distinct from the realName. If realName is
                            missing/empty, render the handle alone — no duplicated
                            "(username)" suffix, no tiny grey shadow line. */}
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-foreground truncate">
                            {hit.realName && hit.realName.trim().length > 0
                              ? hit.realName
                              : hit.username}
                          </span>
                          {hit.realName &&
                            hit.realName.trim().length > 0 &&
                            hit.realName !== hit.username && (
                              <span className="text-xs text-muted-foreground truncate">
                                @{hit.username}
                              </span>
                            )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
